/**
 * Sector operations — Schedules and Dues pure helpers.
 * R39 A5.b (plan docs/plans/sector-operations.md §2 SO-P63, SO-P61).
 */

import {
  MAX_ESCALATE_EVERY_MINUTES,
  MAX_GRACE_MINUTES,
  MAX_SCHEDULE_PLACES,
  MAX_SKIP_REASON_LENGTH,
  MIN_ESCALATE_EVERY_MINUTES,
  MIN_SKIP_REASON_LENGTH,
  type CreateScheduleInput,
  type OpsDue,
  type OpsSchedule,
  type OpsScheduleCadence,
  type OpsScheduleCadenceKind,
  type OpsSchedulePlaceTarget,
  type OpsScheduleWindowSpec,
  type OpsShift,
  type UpdateScheduleInput,
} from '../types/ops'
import { getNlsString } from '../nls/nlsRuntime'
import type { NlsKey } from '../nls/strings_nls'
import type { NlsInterpolationValues } from '../nls/nlsTypes'
import { resolveWorkspaceTimeZone } from '../services/repeat/keys'

export type ScheduleNlsTranslator = (
  key: NlsKey,
  values?: NlsInterpolationValues,
) => string

const defaultTranslator: ScheduleNlsTranslator = (key, values) =>
  getNlsString(key, values)

export function minutesToTimeString(minutes: number): string {
  const normalized = Math.max(0, Math.min(1439, Math.floor(minutes)))
  const h = Math.floor(normalized / 60)
  const m = normalized % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export function timeStringToMinutes(timeStr: string): number {
  if (!timeStr) return 0
  const parts = timeStr.trim().split(':')
  if (parts.length < 2) return 0
  const h = parseInt(parts[0], 10) || 0
  const m = parseInt(parts[1], 10) || 0
  return Math.min(1439, Math.max(0, h * 60 + m))
}

export function getLocalizedWeekdayName(isoWeekday: number, locale?: string): string {
  const loc = locale || 'en'
  // 2024-01-01 was a Monday (ISO weekday 1)
  const refDate = new Date(Date.UTC(2024, 0, isoWeekday, 12, 0, 0))
  return new Intl.DateTimeFormat(loc, { weekday: 'long', timeZone: 'UTC' }).format(refDate)
}

export function getLocalizedMonthName(month: number, locale?: string, style: 'short' | 'long' = 'short'): string {
  const loc = locale || 'en'
  // month is 1..12
  const refDate = new Date(Date.UTC(2024, month - 1, 15, 12, 0, 0))
  return new Intl.DateTimeFormat(loc, { month: style, timeZone: 'UTC' }).format(refDate)
}

export function formatAnchorDayKeyShort(dayKey: string, locale?: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return dayKey
  const parts = dayKey.split('-')
  const m = parseInt(parts[1], 10)
  const d = parseInt(parts[2], 10)
  const monthName = getLocalizedMonthName(m, locale, 'short')
  return `${d} ${monthName}`
}

/**
 * Format cadence summary in plain words for every cadence kind (SO-P63).
 */
export function formatCadenceSummary(
  schedule: {
    cadence: OpsScheduleCadence
    window?: OpsScheduleWindowSpec
  },
  options?: {
    shiftName?: string
    shift?: OpsShift
    t?: ScheduleNlsTranslator
    locale?: string
    timezone?: string
  },
): string {
  const t = options?.t ?? defaultTranslator
  const loc = options?.locale || 'en'
  const { cadence, window: win } = schedule
  const startMin = win?.startMinute ?? 0
  const durationMin = win?.durationMinutes ?? 60
  const startStr = minutesToTimeString(startMin)
  const endMin = (startMin + durationMin) % 1440
  const endStr = minutesToTimeString(endMin)
  const timeRange = `${startStr}–${endStr}`

  switch (cadence.kind) {
    case 'PER_SHIFT': {
      const shiftName = options?.shiftName ?? options?.shift?.name ?? ''
      const shiftDuration = options?.shift
        ? options.shift.endMinute > options.shift.startMinute
          ? options.shift.endMinute - options.shift.startMinute
          : 1440 - options.shift.startMinute + options.shift.endMinute
        : undefined

      const isWholeShift =
        win?.durationMinutes === undefined ||
        win?.durationMinutes === 0 ||
        (shiftDuration !== undefined &&
          startMin === 0 &&
          win?.durationMinutes === shiftDuration)

      if (isWholeShift) {
        if (shiftName) {
          return t('NLSOpsCadencePerShiftWhole', { shift: shiftName })
        }
        return t('NLSOpsCadencePerShiftWholeNoShift')
      }

      return t('NLSOpsCadencePerShiftPart', {
        shift: shiftName || 'Shift',
        start: startStr,
        duration: durationMin,
      })
    }

    case 'DAILY':
      return t('NLSOpsCadenceDaily', { timeRange })

    case 'WEEKLY': {
      const weekday = win?.isoWeekday ?? 1
      const dayName = getLocalizedWeekdayName(weekday, loc)
      return t('NLSOpsCadenceWeekly', { dayName, timeRange })
    }

    case 'MONTHLY': {
      const dom = win?.dayOfMonth ?? 1
      if (dom === -1) {
        return t('NLSOpsCadenceMonthlyLastDay', { timeRange })
      }
      return t('NLSOpsCadenceMonthly', { day: dom, timeRange })
    }

    case 'QUARTERLY': {
      const moq = win?.monthOfQuarter ?? 1
      const dom = win?.dayOfMonth ?? 1
      if (dom === -1) {
        return t('NLSOpsCadenceQuarterlyLastDay', {
          month: moq,
          timeRange,
        })
      }
      return t('NLSOpsCadenceQuarterly', {
        month: moq,
        day: dom,
        timeRange,
      })
    }

    case 'YEARLY': {
      const m = win?.month ?? 1
      const monthName = getLocalizedMonthName(m, loc, 'short')
      const dom = win?.dayOfMonth ?? 1
      if (dom === -1) {
        return t('NLSOpsCadenceYearlyLastDay', {
          monthName,
          timeRange,
        })
      }
      return t('NLSOpsCadenceYearly', {
        day: dom,
        monthName,
        timeRange,
      })
    }

    case 'EVERY_N_DAYS': {
      const interval = cadence.intervalDays ?? 2
      const anchorDate = formatAnchorDayKeyShort(cadence.anchorDayKey ?? '', loc)
      return t('NLSOpsCadenceEveryNDays', {
        interval,
        anchorDate,
        timeRange,
      })
    }

    default:
      return timeRange
  }
}

/**
 * Format window label of a due in the workspace time zone.
 * Shows start–end with day; a window crossing midnight shows both days.
 */
export function formatDueWindowLabel(
  windowStart: number,
  windowEnd: number,
  rawWorkspaceTimezone: unknown,
  locale?: string,
): string {
  const tz = resolveWorkspaceTimeZone(rawWorkspaceTimezone)
  const loc = locale || 'en'

  const dtf = new Intl.DateTimeFormat(loc, {
    timeZone: tz,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const extractParts = (ms: number) => {
    const parts = dtf.formatToParts(new Date(ms))
    let year = ''
    let month = ''
    let day = ''
    let hour = ''
    let minute = ''
    for (const p of parts) {
      if (p.type === 'year') year = p.value
      else if (p.type === 'month') month = p.value
      else if (p.type === 'day') day = p.value
      else if (p.type === 'hour') hour = p.value
      else if (p.type === 'minute') minute = p.value
    }
    // Convert 24:00 to 00:00 if needed by some engines
    if (hour === '24') hour = '00'
    return { year, month, day, hour, minute, dateKey: `${year}-${month}-${day}` }
  }

  const start = extractParts(windowStart)
  const end = extractParts(windowEnd)

  if (start.dateKey === end.dateKey) {
    return `${start.day} ${start.month}, ${start.hour}:${start.minute}–${end.hour}:${end.minute}`
  }

  if (start.year === end.year) {
    return `${start.day} ${start.month}, ${start.hour}:${start.minute} – ${end.day} ${end.month}, ${end.hour}:${end.minute}`
  }

  return `${start.day} ${start.month} ${start.year}, ${start.hour}:${start.minute} – ${end.day} ${end.month} ${end.year}, ${end.hour}:${end.minute}`
}

/**
 * Format a single timestamp moment as localized day + time.
 */
export function formatDateTime(
  timestamp: number,
  rawWorkspaceTimezone?: unknown,
  locale?: string,
): string {
  const tz = resolveWorkspaceTimeZone(rawWorkspaceTimezone)
  const loc = locale || 'en'
  const date = new Date(timestamp)

  const dFmt = new Intl.DateTimeFormat(loc, {
    timeZone: tz,
    day: 'numeric',
    month: 'short',
  })
  const tFmt = new Intl.DateTimeFormat(loc, {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  return `${dFmt.format(date)}, ${tFmt.format(date)}`
}

/**
 * Due-now selection and sort:
 * Status OPEN or MISSED whose window started in the last 7 days or opens in the next 24 h.
 * Sort: MISSED first (newest problems first by windowEnd descending), then OPEN by windowEnd ascending.
 */
export function filterDueNow(
  dues: OpsDue[],
  now: number = Date.now(),
): OpsDue[] {
  const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000
  const twentyFourHoursLater = now + 24 * 60 * 60 * 1000

  const filtered = dues.filter((due) => {
    if (due.status !== 'OPEN' && due.status !== 'MISSED') return false
    return (
      due.windowStart >= sevenDaysAgo && due.windowStart <= twentyFourHoursLater
    )
  })

  return filtered.sort((a, b) => {
    if (a.status === 'MISSED' && b.status !== 'MISSED') return -1
    if (a.status !== 'MISSED' && b.status === 'MISSED') return 1

    if (a.status === 'MISSED' && b.status === 'MISSED') {
      return b.windowEnd - a.windowEnd
    }

    return a.windowEnd - b.windowEnd
  })
}

/**
 * History range filter:
 * Range of 7 or 30 days, shows all statuses, sorted newest first.
 */
export function filterDueHistory(
  dues: OpsDue[],
  rangeDays: 7 | 30 = 7,
  now: number = Date.now(),
): OpsDue[] {
  const windowThreshold = now - rangeDays * 24 * 60 * 60 * 1000

  const filtered = dues.filter(
    (due) => due.windowStart >= windowThreshold && due.windowStart <= now,
  )

  return filtered.sort((a, b) => b.windowStart - a.windowStart)
}

/**
 * Period length in minutes for a given cadence kind.
 */
export function getPeriodLengthMinutes(
  cadenceKind: OpsScheduleCadenceKind,
  intervalDays?: number,
  shiftDurationMinutes?: number,
): number {
  switch (cadenceKind) {
    case 'PER_SHIFT':
      return Math.max(1, shiftDurationMinutes ?? 1440)
    case 'DAILY':
      return 1440
    case 'WEEKLY':
      return 10080 // 7 * 1440
    case 'MONTHLY':
      return 40320 // 28 * 1440
    case 'QUARTERLY':
      return 129600 // 90 * 1440
    case 'YEARLY':
      return 525600 // 365 * 1440
    case 'EVERY_N_DAYS':
      return Math.max(2, intervalDays ?? 2) * 1440
    default:
      return 1440
  }
}

export interface ScheduleFormData {
  templateId: string
  targetMode?: 'EXPLICIT' | 'LEVEL_TARGET'
  placeIds: string[]
  placeTarget?: OpsSchedulePlaceTarget | null
  cadenceKind: OpsScheduleCadenceKind
  shiftId?: string
  intervalDays?: number
  anchorDayKey?: string
  startMinute: number
  durationMinutes: number
  isoWeekday?: number
  dayOfMonth?: number
  monthOfQuarter?: number
  month?: number
  assigneeDutyRoleKey: string
  graceMinutes: number
  escalateEveryMinutes: number
}

export type ScheduleFormErrors = Partial<
  Record<keyof ScheduleFormData | 'placeTargetRoot' | 'placeTargetLevel' | 'general', string>
>

/**
 * Form validation returning per-field errors for every SO-P61 limit.
 */
export function validateScheduleForm(
  data: ScheduleFormData,
  options?: {
    shiftDurationMinutes?: number
    shiftLengthMinutes?: number
    t?: ScheduleNlsTranslator
  },
): { isValid: boolean; errors: ScheduleFormErrors } {
  const t = options?.t ?? defaultTranslator
  const errors: ScheduleFormErrors = {}

  // Template
  if (!data.templateId || !data.templateId.trim()) {
    errors.templateId = t('NLSOpsScheduleErrorTemplateRequired')
  }

  // Places or Place Target (mutually exclusive)
  if (data.targetMode === 'LEVEL_TARGET' || (data.placeTarget && (!data.placeIds || data.placeIds.length === 0))) {
    if (!data.placeTarget?.rootPlaceId) {
      errors.placeTargetRoot = t('NLSOpsScheduleErrorPlacesRequired')
    }
    if (!data.placeTarget?.level) {
      errors.placeTargetLevel = t('NLSOpsScheduleErrorPlacesRequired')
    }
  } else {
    // Places (1–50)
    if (!data.placeIds || data.placeIds.length === 0) {
      errors.placeIds = t('NLSOpsScheduleErrorPlacesRequired')
    } else if (data.placeIds.length > MAX_SCHEDULE_PLACES) {
      errors.placeIds = t('NLSOpsScheduleErrorPlacesMax')
    }
  }

  const shiftLength =
    options?.shiftLengthMinutes ?? options?.shiftDurationMinutes ?? 1440

  // Cadence specific limits
  switch (data.cadenceKind) {
    case 'PER_SHIFT':
      if (!data.shiftId || !data.shiftId.trim()) {
        errors.shiftId = t('NLSOpsScheduleErrorShiftRequired')
      }
      break

    case 'WEEKLY':
      if (
        data.isoWeekday === undefined ||
        data.isoWeekday < 1 ||
        data.isoWeekday > 7 ||
        !Number.isInteger(data.isoWeekday)
      ) {
        errors.isoWeekday = t('NLSOpsScheduleErrorWeekdayRange')
      }
      break

    case 'MONTHLY':
      if (
        data.dayOfMonth === undefined ||
        !Number.isInteger(data.dayOfMonth) ||
        (data.dayOfMonth !== -1 && (data.dayOfMonth < 1 || data.dayOfMonth > 28))
      ) {
        errors.dayOfMonth = t('NLSOpsScheduleErrorDayOfMonthRange')
      }
      break

    case 'QUARTERLY':
      if (
        data.monthOfQuarter === undefined ||
        data.monthOfQuarter < 1 ||
        data.monthOfQuarter > 3 ||
        !Number.isInteger(data.monthOfQuarter)
      ) {
        errors.monthOfQuarter = t('NLSOpsScheduleErrorMonthOfQuarterRange')
      }
      if (
        data.dayOfMonth === undefined ||
        !Number.isInteger(data.dayOfMonth) ||
        (data.dayOfMonth !== -1 && (data.dayOfMonth < 1 || data.dayOfMonth > 28))
      ) {
        errors.dayOfMonth = t('NLSOpsScheduleErrorDayOfMonthRange')
      }
      break

    case 'YEARLY':
      if (
        data.month === undefined ||
        data.month < 1 ||
        data.month > 12 ||
        !Number.isInteger(data.month)
      ) {
        errors.month = t('NLSOpsScheduleErrorMonthRange')
      }
      if (
        data.dayOfMonth === undefined ||
        !Number.isInteger(data.dayOfMonth) ||
        (data.dayOfMonth !== -1 && (data.dayOfMonth < 1 || data.dayOfMonth > 28))
      ) {
        errors.dayOfMonth = t('NLSOpsScheduleErrorDayOfMonthRange')
      }
      break

    case 'EVERY_N_DAYS':
      if (
        data.intervalDays === undefined ||
        data.intervalDays < 2 ||
        data.intervalDays > 365 ||
        !Number.isInteger(data.intervalDays)
      ) {
        errors.intervalDays = t('NLSOpsScheduleErrorIntervalRange')
      }
      if (!data.anchorDayKey || !/^\d{4}-\d{2}-\d{2}$/.test(data.anchorDayKey)) {
        errors.anchorDayKey = t('NLSOpsScheduleErrorAnchorDateRequired')
      }
      break
  }

  if (data.cadenceKind === 'PER_SHIFT') {
    if (
      typeof data.startMinute !== 'number' ||
      !Number.isInteger(data.startMinute) ||
      data.startMinute < 0 ||
      data.startMinute >= shiftLength
    ) {
      errors.startMinute = t('NLSOpsScheduleErrorStartMinuteRange')
    }
    if (
      typeof data.durationMinutes !== 'number' ||
      !Number.isInteger(data.durationMinutes) ||
      data.durationMinutes < 1 ||
      data.startMinute + data.durationMinutes > shiftLength
    ) {
      errors.durationMinutes = t('NLSOpsScheduleErrorDurationRange', {
        max: Math.max(1, shiftLength - data.startMinute),
      })
    }
  } else {
    // Window startMinute (0–1439)
    if (
      typeof data.startMinute !== 'number' ||
      !Number.isInteger(data.startMinute) ||
      data.startMinute < 0 ||
      data.startMinute > 1439
    ) {
      errors.startMinute = t('NLSOpsScheduleErrorStartMinuteRange')
    }

    // Window durationMinutes (1..period length)
    const maxDuration = getPeriodLengthMinutes(
      data.cadenceKind,
      data.intervalDays,
      shiftLength,
    )
    if (
      typeof data.durationMinutes !== 'number' ||
      !Number.isInteger(data.durationMinutes) ||
      data.durationMinutes < 1 ||
      data.durationMinutes > maxDuration
    ) {
      errors.durationMinutes = t('NLSOpsScheduleErrorDurationRange', {
        max: maxDuration,
      })
    }
  }

  // Duty role
  if (!data.assigneeDutyRoleKey || !data.assigneeDutyRoleKey.trim()) {
    errors.assigneeDutyRoleKey = t('NLSOpsScheduleErrorDutyRoleRequired')
  }

  // Grace minutes (0–240)
  if (
    typeof data.graceMinutes !== 'number' ||
    !Number.isInteger(data.graceMinutes) ||
    data.graceMinutes < 0 ||
    data.graceMinutes > MAX_GRACE_MINUTES
  ) {
    errors.graceMinutes = t('NLSOpsScheduleErrorGraceRange')
  }

  // Escalate every minutes (5–1440)
  if (
    typeof data.escalateEveryMinutes !== 'number' ||
    !Number.isInteger(data.escalateEveryMinutes) ||
    data.escalateEveryMinutes < MIN_ESCALATE_EVERY_MINUTES ||
    data.escalateEveryMinutes > MAX_ESCALATE_EVERY_MINUTES
  ) {
    errors.escalateEveryMinutes = t('NLSOpsScheduleErrorEscalateRange')
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Compare an existing schedule with form values field by field.
 * Returns only changed fields. If nothing changed, returns empty object {}.
 */
export function diffScheduleInput(
  schedule: OpsSchedule,
  form: Partial<CreateScheduleInput>,
): Partial<UpdateScheduleInput> {
  const diff: Partial<UpdateScheduleInput> = {}

  if (form.placeIds !== undefined) {
    const sPlace = [...(schedule.placeIds ?? [])].sort().join(',')
    const fPlace = [...(form.placeIds ?? [])].sort().join(',')
    if (sPlace !== fPlace) {
      diff.placeIds = form.placeIds
    }
  }

  if (form.placeTarget !== undefined) {
    const sTarget = schedule.placeTarget
      ? `${schedule.placeTarget.rootPlaceId}:${schedule.placeTarget.level}`
      : ''
    const fTarget = form.placeTarget
      ? `${form.placeTarget.rootPlaceId}:${form.placeTarget.level}`
      : ''
    if (sTarget !== fTarget) {
      diff.placeTarget = form.placeTarget
    }
  }

  const normAssignee = (v?: string | null) => (v ? v.trim() : '')
  if (
    form.assigneeDutyRoleKey !== undefined &&
    normAssignee(form.assigneeDutyRoleKey) !==
      normAssignee(schedule.assigneeDutyRoleKey)
  ) {
    diff.assigneeDutyRoleKey = normAssignee(form.assigneeDutyRoleKey)
  }

  if (
    form.graceMinutes !== undefined &&
    form.graceMinutes !== schedule.graceMinutes
  ) {
    diff.graceMinutes = form.graceMinutes
  }

  if (
    form.escalateEveryMinutes !== undefined &&
    form.escalateEveryMinutes !== schedule.escalateEveryMinutes
  ) {
    diff.escalateEveryMinutes = form.escalateEveryMinutes
  }

  if (form.window !== undefined) {
    const sWin = schedule.window || {}
    const fWin = form.window
    const sStart = sWin.startMinute ?? 0
    const fStart = fWin.startMinute ?? 0
    const sDur = sWin.durationMinutes ?? 60
    const fDur = fWin.durationMinutes ?? 60
    const sWk = sWin.isoWeekday ?? null
    const fWk = fWin.isoWeekday ?? null
    const sDom = sWin.dayOfMonth ?? null
    const fDom = fWin.dayOfMonth ?? null
    const sMoq = sWin.monthOfQuarter ?? null
    const fMoq = fWin.monthOfQuarter ?? null
    const sM = sWin.month ?? null
    const fM = fWin.month ?? null

    if (
      sStart !== fStart ||
      sDur !== fDur ||
      sWk !== fWk ||
      sDom !== fDom ||
      sMoq !== fMoq ||
      sM !== fM
    ) {
      diff.window = form.window
    }
  }

  if (form.cadence !== undefined) {
    const sCad = schedule.cadence
    const fCad = form.cadence
    if (sCad.kind !== fCad.kind) {
      diff.cadence = form.cadence
    } else {
      let cadChanged = false
      if (sCad.kind === 'PER_SHIFT' && fCad.kind === 'PER_SHIFT') {
        if (sCad.shiftId !== fCad.shiftId) cadChanged = true
      } else if (
        sCad.kind === 'EVERY_N_DAYS' &&
        fCad.kind === 'EVERY_N_DAYS'
      ) {
        if (
          sCad.intervalDays !== fCad.intervalDays ||
          sCad.anchorDayKey !== fCad.anchorDayKey
        ) {
          cadChanged = true
        }
      }
      if (cadChanged) {
        diff.cadence = form.cadence
      }
    }
  }

  return diff
}

/**
 * Validate skip reason (3–500 chars after trim).
 */
export function validateSkipReason(
  reason: string,
  t: ScheduleNlsTranslator = defaultTranslator,
): string | null {
  const trimmed = reason ? reason.trim() : ''
  if (
    trimmed.length < MIN_SKIP_REASON_LENGTH ||
    trimmed.length > MAX_SKIP_REASON_LENGTH
  ) {
    return t('NLSOpsScheduleErrorSkipReasonLength')
  }
  return null
}
